import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ClearedPaywall.module.scss"

/**
 * ClearedPaywall — an original, self-contained ONBOARDING paywall.
 *
 * Divergence brief (claude-paywalls/_ledger.md, concept doc claude-paywalls/cleared.md).
 * Active style directive this run: `focus on security and privacy`.
 *
 * Concept: getting SECURITY CLEARANCE for your own life. A redacted contact
 * sheet of the user's photos sits under a live ACCESS LEDGER — the list of
 * everyone who can see photos on a normal phone. Press the GRANT CLEARANCE
 * thumb-stamp: an ink "EYES ONLY — YOU" stamp thunks down, the black redaction
 * bars retract to reveal the memories for the user, and the ledger strikes every
 * onlooker off, one by one, down to a single name: You.
 *
 *   - Layout:      a dossier / case-file — monospace case header, redacted
 *                  contact sheet, struck-through access ledger. No hero image.
 *   - Interaction: one decisive STAMP triggers a cascade audit (not scroll, not
 *                  hold-to-reveal, not drag, not carousel). Auto-runs after a few
 *                  seconds so passive viewers still see the payoff.
 *   - Register:    confident, official, a little wry — relief, not fear.
 *   - Art:         editorial / brutalist; manila paper + ink; brand blue reused
 *                  as the clearance ink. Deliberately light, not another dark vault.
 *   - Value frame: access control — who can see your photos now → only you.
 *
 * Honest: every onlooker is one the real product addresses (own Face ID / PIN
 * lock; photos moved out of the shared library). Strictly additive — no
 * data-loss claim, no fake urgency. Yearly is the featured, default plan.
 */

// Redacted contact sheet — reuse existing personal-memory photos.
const SHEET = ["m03", "m07", "m12", "m01", "m09", "m15", "m05", "m18", "m11"]

// The access ledger — every onlooker the vault genuinely shuts out.
// (Own Face ID / PIN lock; photos moved out of the shared photo library.)
const ONLOOKERS = [
  "Anyone who picks up your phone",
  "Apps you've granted photo access",
  "Guests in your shared albums",
  "Eyes scrolling past your camera roll",
  "A phone that's lost or stolen",
]

// What the clearance actually covers — real product features, framed as perks.
const COVERS = [
  { k: "lock", label: "Locked with Face ID / PIN" },
  { k: "move", label: "Kept out of your camera roll" },
  { k: "alert", label: "Break-in alerts if someone tries" },
  { k: "decoy", label: "A decoy vault for prying hands" },
  { k: "backup", label: "Private, encrypted cloud backup" },
]

type Phase = "pending" | "clearing" | "cleared"
type PlanKey = "yearly" | "monthly"

const ClearedPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [phase, setPhase] = useState<Phase>("pending")
  const [revoked, setRevoked] = useState(0)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)
  const clearTimers = () => {
    timers.current.forEach((id) => window.clearTimeout(id))
    timers.current = []
  }

  const grant = useCallback(() => {
    setPhase((p) => {
      if (p !== "pending") return p
      native.logMixpanelEvent("html_cleared_grant")
      if (reduceMotion) {
        setRevoked(ONLOOKERS.length)
        return "cleared"
      }
      // Stagger the ledger strike-throughs, then settle to cleared.
      ONLOOKERS.forEach((_, i) => {
        pushTimer(window.setTimeout(() => setRevoked(i + 1), 520 + i * 300))
      })
      pushTimer(
        window.setTimeout(
          () => setPhase("cleared"),
          520 + ONLOOKERS.length * 300 + 240,
        ),
      )
      return "clearing"
    })
  }, [reduceMotion])

  // Reveal the close affordance after a beat; auto-run the audit if the user
  // hasn't pressed, so passive viewers still see the payoff.
  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setShowClose(true), 2600))
    pushTimer(window.setTimeout(() => grant(), 4200))
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cleared = phase === "cleared"
  const active = phase !== "pending"
  const remaining = cleared ? 0 : ONLOOKERS.length - revoked

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    if (selectedProduct) native.subscribe(selectedProduct)
  }

  return (
    <div className={`${styles.root} ${cleared ? styles.rootCleared : ""}`}>
      <div className={styles.paper} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      {/* ---------- case header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.3 12.1l2 2 3.5-3.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium clearance</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- case tag + title ---------- */}
      <div className={styles.intro}>
        <span className={styles.caseTag}>
          <span className={styles.caseNo}>CASE&nbsp;#PPV-001</span>
          <span
            className={`${styles.caseState} ${cleared ? styles.caseStateOk : ""}`}
          >
            {cleared ? "CLEARED" : "CLEARANCE PENDING"}
          </span>
        </span>
        <h1 className={styles.title}>
          {cleared ? (
            <>
              Cleared for
              <br />
              <span className={styles.titleInk}>one set of eyes.</span>
            </>
          ) : (
            <>
              Your photos,
              <br />
              <span className={styles.titleInk}>classified.</span>
            </>
          )}
        </h1>
        <p className={styles.sub}>
          {cleared
            ? "Your memories are sealed to a single clearance — yours. Lock in Premium and keep it that way."
            : "Right now, more eyes reach your camera roll than you think. Grant your clearance and lock every private memory to one person: you."}
        </p>
      </div>

      {/* ---------- the dossier: redacted sheet ---------- */}
      <div className={styles.dossier}>
        <div
          className={styles.sheet}
          role="img"
          aria-label="A contact sheet of your private photos"
        >
          {SHEET.map((id, i) => (
            <div key={id} className={styles.frame}>
              <div
                className={styles.photo}
                style={{ backgroundImage: `url(/byheart/${id}.jpg)` }}
              />
              <span
                className={`${styles.bar} ${active ? styles.barOff : ""}`}
                style={{ transitionDelay: `${active ? 120 + i * 55 : 0}ms` }}
                aria-hidden="true"
              >
                <span className={styles.barText}>REDACTED</span>
              </span>
            </div>
          ))}

          {/* the ink stamp thunks down on clearance */}
          {active && (
            <motion.div
              className={styles.stamp}
              initial={
                reduceMotion
                  ? { opacity: 1, scale: 1, rotate: -8 }
                  : { opacity: 0, scale: 1.7, rotate: -26 }
              }
              animate={{ opacity: 1, scale: 1, rotate: -8 }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 520, damping: 15, delay: 0.12 }
              }
              aria-hidden="true"
            >
              <span className={styles.stampInner}>
                <span className={styles.stampTop}>EYES ONLY</span>
                <span className={styles.stampBig}>YOU</span>
                <span className={styles.stampBot}>PPV · CLEARED</span>
              </span>
            </motion.div>
          )}
        </div>

        {/* ---------- access ledger ---------- */}
        <div className={styles.ledger}>
          <div className={styles.ledgerHead}>
            <span className={styles.ledgerTitle}>WHO CAN SEE THESE</span>
            <span
              className={`${styles.counter} ${cleared ? styles.counterOk : ""}`}
              aria-live="polite"
            >
              {cleared ? (
                <>
                  <span className={styles.counterDot} />
                  ONLY YOU
                </>
              ) : (
                `${remaining} CAN`
              )}
            </span>
          </div>

          <ul className={styles.ledgerList}>
            {ONLOOKERS.map((name, i) => {
              const off = i < revoked || cleared
              return (
                <li
                  key={name}
                  className={`${styles.ledgerItem} ${off ? styles.ledgerOff : ""}`}
                >
                  <span className={styles.ledgerIcon} aria-hidden="true">
                    {off ? (
                      <svg viewBox="0 0 24 24" width="13" height="13">
                        <path
                          d="M5 5l14 14M19 5L5 19"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="14" height="14">
                        <path
                          d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        />
                        <circle cx="12" cy="12" r="2.4" fill="currentColor" />
                      </svg>
                    )}
                  </span>
                  <span className={styles.ledgerName}>{name}</span>
                  <span className={styles.ledgerState}>
                    {off ? "REVOKED" : "can see"}
                  </span>
                </li>
              )
            })}
            <li
              className={`${styles.ledgerItem} ${styles.ledgerYou} ${cleared ? styles.ledgerYouOn : ""}`}
            >
              <span className={styles.ledgerIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path
                    d="M12 12.6a3.4 3.4 0 100-6.8 3.4 3.4 0 000 6.8zM5.5 19.4c0-3 2.9-4.9 6.5-4.9s6.5 1.9 6.5 4.9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.ledgerName}>You, with your key</span>
              <span className={styles.ledgerState}>cleared</span>
            </li>
          </ul>
        </div>

        {/* ---------- the stamp control (hero interaction) ---------- */}
        {!active ? (
          <button className={styles.grant} onClick={grant}>
            <span className={styles.grantRing} aria-hidden="true" />
            <span className={styles.grantThumb} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path
                  d="M12 21c-.6-.3-5-2.6-5-8.2V6.9a1 1 0 01.6-.9C9 5.4 10.4 4.7 12 3.6c1.6 1.1 3 1.8 4.4 2.4a1 1 0 01.6.9v5.9c0 5.6-4.4 7.9-5 8.2z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8v4.4M12 12.4l2.6 2.6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.grantCopy}>
              <span className={styles.grantMain}>Grant clearance</span>
              <span className={styles.grantHint}>
                Press to seal these to your eyes only
              </span>
            </span>
          </button>
        ) : (
          <div
            className={`${styles.clearedNote} ${cleared ? styles.clearedNoteOn : ""}`}
            aria-live="polite"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
              <path
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {cleared
              ? "Clearance granted — visible to you alone"
              : "Revoking access…"}
          </div>
        )}
      </div>

      {/* ---------- what your clearance covers ---------- */}
      <div className={styles.covers}>
        <span className={styles.coversHead}>Your clearance covers</span>
        <div className={styles.coversList}>
          {COVERS.map((c) => (
            <span key={c.k} className={styles.cover}>
              <span className={styles.coverTick} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="11" height="11">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {c.label}
            </span>
          ))}
        </div>
        <p className={styles.coversFoot}>
          The vault only adds a locked, private copy — your photos stay right
          where they are on your phone too.
        </p>
      </div>

      {/* ---------- plans — yearly featured ---------- */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyTop}>
            <span className={styles.yearlyName}>Yearly clearance</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>SAVE {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyRow}>
            <span className={styles.yearlyPerMo}>
              <span className={styles.yearlyBig}>{yearlyPerMonth}</span>
              <span className={styles.yearlyUnit}>/mo</span>
            </span>
            <span className={styles.yearlyBilled}>
              billed {yearlyPrice}/yr · 7-day free trial
            </span>
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      {/* ---------- sticky CTA dock ---------- */}
      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Seal my clearance for a year"
              : "Start my clearance"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7-day free trial, then ${yearlyPrice}/yr (${yearlyPerMonth}/mo) · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default ClearedPaywall
